/**
 * Tarot credit localStorage helpers (HORO-10327, revised HORO-10562).
 *
 * Pure helpers — no React, no Redux. Lives outside the component tree
 * so `useAuth` (which fires the carry-over on sign-in) can import these
 * without dragging in `authSlice`, which would create a circular module
 * graph (useAuth → tarotCredits → authSlice → ... → useAuth) and
 * trigger a "Cannot access 'authReducers' before initialization" error.
 *
 * The React hook `useTarotCredits` lives in
 *   components/common/chat/tarot/tarotCredits.js
 * and is the only place that touches authSlice.
 */
import { MONTHLY_TAROT_LIMIT, STORAGE_KEY_DAILY_TAROT } from '../const';

export const nextUtcMidnightIso = (from = new Date()) => {
    const next = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + 1, 0, 0, 0, 0));
    return next.toISOString();
};

const safeParse = (raw) => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

/**
 * Read the anon counters from localStorage, rolling the window if the
 * stored `resetAt` has passed. Returns the canonical `{ used, resetAt }`
 * shape used by both the hook and the send-path header serialisation.
 */
export const readAnonTarotCredits = () => {
    if (typeof window === 'undefined') {
        return { used: 0, resetAt: nextUtcMidnightIso() };
    }
    const parsed = safeParse(window.localStorage.getItem(STORAGE_KEY_DAILY_TAROT));
    if (!parsed || typeof parsed.used !== 'number' || typeof parsed.resetAt !== 'string') {
        return { used: 0, resetAt: nextUtcMidnightIso() };
    }
    const resetAtMs = new Date(parsed.resetAt).getTime();
    if (Number.isNaN(resetAtMs) || Date.now() >= resetAtMs) {
        return { used: 0, resetAt: nextUtcMidnightIso() };
    }
    return {
        used: Math.max(0, Math.min(MONTHLY_TAROT_LIMIT, Math.floor(parsed.used))),
        resetAt: parsed.resetAt
    };
};

export const writeAnonTarotCredits = ({ used, resetAt }) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY_DAILY_TAROT, JSON.stringify({ used, resetAt }));
    } catch {
        // sandboxed iframes / private modes can throw — best-effort write
    }
};

/**
 * Intentionally has no callers. Sign-in used to call it once the counter had
 * been carried over to the user document, which made signing in and straight
 * back out a free refill of the anonymous pool. Don't wire it back into the
 * auth flow — the counter is a per-device daily limit and is meant to outlive
 * any single session. Kept as the counterpart to read/write for tests and for
 * a deliberate reset.
 */
export const clearAnonTarotCredits = () => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(STORAGE_KEY_DAILY_TAROT);
    } catch {
        // ignore
    }
};

/**
 * Header value for the `x-anon-tarot-credits` request header sent on
 * each tarot `reading`-phase chat send for anon users.
 */
export const anonTarotCreditsHeaderValue = () => {
    const { used, resetAt } = readAnonTarotCredits();
    return JSON.stringify({ used, resetAt });
};
